import { useState, type FormEvent } from 'react';
import { changePassword, resendVerification, updateMe } from '../api/auth';
import { toFormErrors, type FormErrors } from '../auth/errors';
import { useAuth } from '../auth/useAuth';
import type { Account } from '../api/auth';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Badge, Button, Card, Input, LoadingState } from '../components/ui';
import { MyReports } from '../components/posts/MyReports';
import './AccountPage.css';

const NO_ERRORS: FormErrors = { fields: {}, message: null };

/** `/account` — the signed-in user's own settings (GET/PATCH /api/v1/me). */
export function AccountPage() {
  const { account, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingState label="Loading your account…" />;
  }

  if (!account) {
    return null;
  }

  // Keyed by id so the form state is built from a loaded account, never from
  // an empty placeholder, and it resets cleanly if the signed-in user changes.
  return <AccountSettings key={account.id} account={account} />;
}

function AccountSettings({ account }: { account: Account }) {
  // Signing out is the shell's, now that this page sits inside it: one way to
  // do it, in the place it is on every other screen.
  const { setAccount } = useAuth();

  const [profile, setProfile] = useState({
    display_name: account.display_name,
    handle: account.handle,
    bio: account.bio,
  });
  const [profileErrors, setProfileErrors] = useState<FormErrors>(NO_ERRORS);
  const [profileSaved, setProfileSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const [passwordErrors, setPasswordErrors] = useState<FormErrors>(NO_ERRORS);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [verificationSent, setVerificationSent] = useState(false);

  async function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setProfileErrors(NO_ERRORS);
    setProfileSaved(false);
    setSavingProfile(true);

    try {
      setAccount(await updateMe(profile));
      setProfileSaved(true);
    } catch (error) {
      setProfileErrors(toFormErrors(error));
    } finally {
      setSavingProfile(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordErrors(NO_ERRORS);
    setPasswordChanged(false);
    setSavingPassword(true);

    try {
      await changePassword(passwords.current, passwords.next);
      setPasswords({ current: '', next: '' });
      setPasswordChanged(true);
    } catch (error) {
      setPasswordErrors(toFormErrors(error));
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    // Inside the shell like every other screen. Until Phase 27 it was the one
    // page without the product's own header, navigation or footer — so a person
    // who opened their settings had no way back except the browser's, and a
    // screen reader found a page whose content sat in no landmark at all.
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>Your account</h1>
            <p className="sc-account__email">
              {account.email}{' '}
              {account.is_email_verified ? (
                <Badge tone="success" srPrefix="Email status">
                  ✅ Confirmed
                </Badge>
              ) : (
                <Badge tone="warning" srPrefix="Email status">
                  ⚠️ Not confirmed
                </Badge>
              )}
            </p>
          </div>
        </div>
      }
    >
      {!account.is_email_verified && (
        <Alert tone="warning" title="Confirm your email address">
          <p>Publishing and sharing stay locked until your address is confirmed.</p>
          {verificationSent ? (
            <span>A new confirmation link is on its way.</span>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void resendVerification(account.email).then(() => setVerificationSent(true));
              }}
            >
              Send a new link
            </Button>
          )}
        </Alert>
      )}

      <Card title="Profile" description="How you appear across Simple Coffee.">
        <form className="sc-form" onSubmit={handleProfileSubmit} noValidate>
          {profileErrors.message && <Alert tone="danger">{profileErrors.message}</Alert>}
          {profileSaved && <Alert tone="success">Your profile is up to date.</Alert>}

          <Input
            label="Display name"
            value={profile.display_name}
            onChange={(event) =>
              setProfile((current) => ({ ...current, display_name: event.target.value }))
            }
            error={profileErrors.fields['display_name']}
          />

          <Input
            label="Handle"
            prefix="@"
            value={profile.handle}
            onChange={(event) =>
              setProfile((current) => ({ ...current, handle: event.target.value }))
            }
            hint="Changing this changes the link to your profile."
            error={profileErrors.fields['handle']}
          />

          <Input
            label="Bio"
            maxLength={280}
            value={profile.bio}
            onChange={(event) => setProfile((current) => ({ ...current, bio: event.target.value }))}
            hint="Up to 280 characters."
            error={profileErrors.fields['bio']}
          />

          <div className="sc-form__actions">
            <Button type="submit" isLoading={savingProfile} loadingLabel="Saving profile">
              Save changes
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Password" description="Changing it signs out every other device.">
        <form className="sc-form" onSubmit={handlePasswordSubmit} noValidate>
          {passwordErrors.message && <Alert tone="danger">{passwordErrors.message}</Alert>}
          {passwordChanged && <Alert tone="success">Your password has been updated.</Alert>}

          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            value={passwords.current}
            onChange={(event) =>
              setPasswords((current) => ({ ...current, current: event.target.value }))
            }
            error={passwordErrors.fields['current_password']}
          />

          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            value={passwords.next}
            onChange={(event) =>
              setPasswords((current) => ({ ...current, next: event.target.value }))
            }
            error={passwordErrors.fields['new_password']}
          />

          <div className="sc-form__actions">
            <Button type="submit" isLoading={savingPassword} loadingLabel="Updating password">
              Update password
            </Button>
          </div>
        </form>
      </Card>

      <MyReports />
    </AppShell>
  );
}
